import React, { FC } from "react";
import { connect } from "react-redux";
import {
  AccountType,
  INavigationHeaderProps,
  IPerformerSearchResult,
  LocalizationUtils,
  NavigationHeader,
  getNavHeaderLinks,
  getCDSearchConfig,
} from "component-library";
import { AddOnEntitlements, PremiumEntitlements } from "shared-types";
import {
  authorizationService,
  checkIsAgent,
  checkIsCD,
  UserAttributes,
} from "shared-auth";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import config from "config/global";

import { IApplicationState } from "store/index";
import { hidePopup, setLocale, showPopup } from "store/ui/ui.actions";
import { HidePopup, SetLocale, ShowPopup } from "store/ui/ui.types";

import { MapDispatchToProps } from "types/utilities";

import { IUser } from "models/user";

import "./HeaderContainer.scss";
import { useTranslation } from "i18n";

type LocalizationChangeHandler =
  INavigationHeaderProps["handlers"]["onLocalizationChangedHandler"];

export interface IHeaderContainerActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setLocale: SetLocale;
}

export interface IHeaderContainerStoreProps {
  user: IUser | undefined;
  locale: string;
}

export interface IHeaderContainerProps
  extends IHeaderContainerStoreProps,
    IHeaderContainerActions {
  menuContainer: React.RefObject<HTMLDivElement>;
  popupContainer: React.RefObject<HTMLDivElement>;
  agentQuickSearchPlaceholder: string;
}

export const HeaderContainer: FC<
  React.PropsWithChildren<IHeaderContainerProps>
> = (props) => {
  const { t } = useTranslation();
  const memberGroup = authorizationService.getUserGroup();

  // replace with checkIsCastingBBC when authorizationService for this app
  const isCastingBBC =
    authorizationService.getUserAttribute(UserAttributes.IdentityProvider) ===
    "ipcheck";
  const isPremiumLEGACY = authorizationService.doesUserHaveEntitlements(
    PremiumEntitlements.Premium_Site_Access
  );
  const isAddonPerformer =
    authorizationService.doesUserHaveEntitlements(
      AddOnEntitlements.AddOnBundle
    ) || isPremiumLEGACY;
  const {
    [FEATURE_FLAGS.SelfTapes]: isSelfTapesEnabled,
    [FEATURE_FLAGS.SelfTapesAgentPerformer]: isSelfTapesAgentPerformerEnabled,
    [FEATURE_FLAGS.PerformerSearch]: isPremiumSearchPerformerFeatureFlagOn,
    [FEATURE_FLAGS.AgencySettings]: isAgencySettingsFlagEnabled,
    [FEATURE_FLAGS.FilmmakersPhase4]: isFilmmakersPhase4Enabled,
  } = useLaunchDarklyFlags([
    FEATURE_FLAGS.SelfTapes,
    FEATURE_FLAGS.SelfTapesAgentPerformer,
    FEATURE_FLAGS.PerformerSearch,
    FEATURE_FLAGS.AgencySettings,
    FEATURE_FLAGS.FilmmakersPhase4,
  ]);

  const { lhNavBottomLinks, headerLabels, localPopUpText, mainNav } =
    getNavHeaderLinks(memberGroup, {
      isCastingBBC,
      isAddonPerformer,
      isAgencySettingsFlagEnabled,
      isSelfTapesEnabled,
      isSelfTapesAgentPerformerEnabled,
      isFilmmakersPhase4Enabled,
    });

  const { menuContainer, popupContainer, user, agentQuickSearchPlaceholder } =
    props;

  const handleLocalizationChange: LocalizationChangeHandler = (
    language,
    location
  ) => {
    props.setLocale(LocalizationUtils.getLocalizationCode(language, location));
  };
  const accountPageUrl = headerLabels.myAccountLink;

  const handleSignOutClick = () => {
    authorizationService.signoutPortalRedirect();
  };

  const handleAccountClick = (): void => {
    window.location.assign(accountPageUrl);
  };

  const handlers: INavigationHeaderProps["handlers"] = {
    onSignOutHandler: handleSignOutClick,
    onClickAccountHandler: handleAccountClick,
    onLocalizationChangedHandler: handleLocalizationChange,
  };

  const performerSearchProps = {
    content: { inputPlaceholder: agentQuickSearchPlaceholder },
    showMoreResults: (searchTerm: string) =>
      (window.location.href = `${config.siteRootUrl}jobs/performers/search/${searchTerm}`),
    loadSearchResults: () => new Promise<IPerformerSearchResult[]>(() => []),
  };

  const shouldShowSearchPerformer = () => {
    if (checkIsCD()) {
      return getCDSearchConfig();
    }

    if (checkIsAgent()) {
      return performerSearchProps;
    }

    if (!isPremiumSearchPerformerFeatureFlagOn) {
      return undefined;
    }

    const isPerformer = user?.type === AccountType.Performer;
    const isYoungPerformer = user?.memberGroups?.some(
      (group) =>
        group.group === "performer" && group.subType === "youngPerformer"
    );
    const userHasEntitlements = authorizationService.doesUserHaveEntitlements(
      PremiumEntitlements.Premium_Performer_Search
    );

    // only premium main performers with flag on
    const mainPerformerCanSearchPerformers =
      isPerformer && !isYoungPerformer && userHasEntitlements;

    if (mainPerformerCanSearchPerformers) {
      return performerSearchProps;
    }

    return undefined;
  };

  return (
    <NavigationHeader
      bottomNavLinks={lhNavBottomLinks}
      menuContainer={menuContainer}
      popupContainer={popupContainer}
      accountType={user?.type ?? AccountType.DefaultUser}
      headerLabels={headerLabels}
      logoRedirectionLink={config.marketingSiteUrl}
      links={[]}
      signedIn={true}
      countryISOCode={
        LocalizationUtils.getUserLocalizationPreference().location
      }
      handlers={handlers}
      menuClassName="c-header-section__navigation-menu"
      userName={user?.username ?? "Loading..."}
      mainNavData={mainNav}
      userLinkUrl={accountPageUrl}
      localizationTexts={localPopUpText}
      //@ts-expect-error - check when working in this file
      performerSearchProps={shouldShowSearchPerformer()}
      t={t}
    />
  );
};

const mapDispatchToProps: MapDispatchToProps<IHeaderContainerActions> = {
  showPopup,
  hidePopup,
  setLocale,
};

const mapStateToProps = (
  state: IApplicationState
): IHeaderContainerStoreProps => {
  const user = state.user.data;

  return {
    user,
    locale: state.ui.locale,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HeaderContainer);
